import { rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { BriefingChange, BriefingShiftSnapshot } from '@reditelji/shared';
import { usePendingChanges, useVolunteerMutations, type VolunteerRow } from '../api/volunteers';
import { m2t } from '../lib/ui';
import { CARD, MONO, SECTION_LABEL as LBL } from './ui';

/**
 * What the coordinator has changed since this volunteer's last publication, and
 * the two ways out of it (SPEC §1.1F).
 *
 * Her own briefing page deliberately never shows this pairing — the pin against
 * the working state is a draft. The coordinator is the one who has to see it,
 * because the choice is theirs: send the new schedule, or put the old one back.
 */

const KIND_LABEL: Record<BriefingChange['kind'], string> = {
  time: 'čas',
  meet: 'zbor',
  post: 'mesto',
  added: 'dodano',
  removed: 'odvzeto',
  instr: 'navodila',
  photo: 'fotografije',
};

const KIND_COLOUR: Record<BriefingChange['kind'], string> = {
  time: 'oklch(0.42 0.09 245)',
  meet: 'oklch(0.42 0.09 245)',
  post: 'oklch(0.42 0.09 285)',
  added: 'oklch(0.38 0.08 155)',
  removed: 'oklch(0.45 0.12 25)',
  instr: 'oklch(0.55 0.012 90)',
  photo: 'oklch(0.55 0.012 90)',
};

/**
 * A time, with its day in front of it when the move crossed one — otherwise a
 * shift pushed from Friday to Sunday at the same hour reads "07:00 → 07:00".
 */
const times = (s: Partial<BriefingShiftSnapshot>, withDay = false) =>
  s.fromMin === undefined || s.toMin === undefined
    ? ''
    : `${withDay && s.dayLabel ? `${s.dayLabel} ` : ''}${m2t(s.fromMin)}–${m2t(s.toMin)}`;

const meet = (s: Partial<BriefingShiftSnapshot>) =>
  [s.meetMin === null || s.meetMin === undefined ? '—' : m2t(s.meetMin), s.meetPlace]
    .filter(Boolean)
    .join(' · ');

const place = (s: Partial<BriefingShiftSnapshot>) =>
  [s.postCode, s.postMicro].filter(Boolean).join(' · ');

/** The was → now of one change, in the words the kind calls for. */
function wasNow(c: BriefingChange): { was: string; now: string; note?: string } {
  switch (c.kind) {
    case 'time': {
      const moved = Boolean(c.was?.dayLabel && c.now?.dayLabel && c.was.dayLabel !== c.now.dayLabel);
      return { was: times(c.was ?? {}, moved), now: times(c.now ?? {}, moved) };
    }
    case 'meet':
      return { was: meet(c.was ?? {}), now: meet(c.now ?? {}) };
    case 'post':
      return { was: place(c.was ?? {}), now: place(c.now ?? {}) };
    case 'added':
      return { was: '', now: times(c.shift), note: 'te izmene še ni videla' };
    case 'removed':
      return { was: times(c.shift), now: '', note: 'na njeni strani še vedno piše, da jo ima' };
    default:
      return { was: '', now: '', note: 'mehka sprememba — vidi jo že zdaj, povrnitev je ne zajame' };
  }
}

const dateLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('sl-SI', { day: 'numeric', month: 'numeric' });

export function PendingChangesCard({ volunteer: v }: { volunteer: VolunteerRow }) {
  const pending = usePendingChanges(v.id);
  const mut = useVolunteerMutations();

  const data = pending.data;
  if (!data || !data.pin || data.changes.length === 0) return null;

  // Soft changes belong to the post, so a card holding only those has nothing
  // to give back — the button must not invite a click the server refuses.
  const blocked = data.blockers.length > 0 || data.revertable === 0;
  const busy = mut.publish.isPending || mut.revert.isPending;
  // A blocker with no shift of its own belongs to the whole card.
  const loose = data.blockers.filter((b) => !b.shiftId);

  return (
    <div style={{ ...CARD, padding: 14, borderColor: 'var(--rr-warn-border)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={LBL}>NEOBJAVLJENE SPREMEMBE</span>
        <span style={{ fontFamily: MONO, fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>
          {data.changes.length}
        </span>
      </div>
      <div style={{ fontSize: rem(11.5), color: 'oklch(0.55 0.012 90)', marginBottom: 10 }}>
        objavljeno v{data.pin.version} · {dateLabel(data.pin.sentAt)} · delovna različica v
        {data.scheduleVersion}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {data.changes.map((c, i) => {
          const { was, now, note } = wasNow(c);
          const own = data.blockers.filter((b) => b.shiftId === c.shift.shiftId);
          const soft = !c.breaking;
          return (
            <div
              key={`${c.kind}-${c.shift.shiftId}-${i}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '68px 1fr',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 7,
                background: own.length ? 'oklch(0.99 0.012 25)' : 'oklch(0.995 0.002 95)',
                border: `1px ${soft ? 'dashed' : 'solid'} ${
                  own.length ? 'oklch(0.9 0.045 25)' : 'oklch(0.93 0.006 95)'
                }`,
              }}
            >
              <span
                style={{
                  fontSize: rem(10.5),
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: KIND_COLOUR[c.kind],
                }}
              >
                {KIND_LABEL[c.kind]}
              </span>
              <span style={{ minWidth: 0 }}>
                <div style={{ fontSize: rem(11.5), color: 'oklch(0.45 0.012 90)' }}>
                  {c.shift.dayLabel} · {place(c.shift)}
                </div>
                {was || now ? (
                  <div style={{ fontFamily: MONO, fontSize: rem(12.5), marginTop: 1 }}>
                    {was ? (
                      <span
                        style={{
                          color: 'oklch(0.5 0.012 90)',
                          textDecoration: 'line-through',
                          textDecorationColor: 'oklch(0.75 0.1 25)',
                        }}
                      >
                        {was}
                      </span>
                    ) : null}
                    {was && now ? <span style={{ color: 'oklch(0.6 0.012 90)' }}> → </span> : null}
                    {now ? <span style={{ fontWeight: 600 }}>{now}</span> : null}
                  </div>
                ) : null}
                {note ? (
                  <div
                    style={{
                      fontSize: rem(11.5),
                      color: 'oklch(0.52 0.012 90)',
                      fontStyle: soft ? 'italic' : undefined,
                      marginTop: 2,
                    }}
                  >
                    {note}
                  </div>
                ) : null}
                {own.map((b) => (
                  <div
                    key={b.kind}
                    style={{ fontSize: rem(11.5), color: 'oklch(0.45 0.12 25)', marginTop: 4 }}
                  >
                    ✕ {b.detail}
                  </div>
                ))}
              </span>
            </div>
          );
        })}
      </div>

      {loose.map((b) => (
        <div
          key={b.kind}
          style={{ fontSize: rem(11.5), color: 'oklch(0.45 0.12 25)', marginTop: 8 }}
        >
          ✕ {b.detail}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            mut.publish.mutate(v.id, {
              onSuccess: () => notifications.show({ message: 'Objavljeno.' }),
            })
          }
          style={{
            ...BTN,
            background: 'oklch(0.35 0.05 245)',
            borderColor: 'oklch(0.35 0.05 245)',
            color: 'oklch(0.99 0 0)',
          }}
        >
          Objavi spremembe
        </button>
        <button
          type="button"
          disabled={busy || blocked}
          title={
            data.blockers[0]?.detail ??
            (data.revertable === 0 ? 'Te spremembe niso izmene te osebe.' : undefined)
          }
          onClick={() =>
            mut.revert.mutate(v.id, {
              onSuccess: (res) =>
                notifications.show({
                  message:
                    res.confirmation === 'potrjen'
                      ? 'Povrnjeno na objavljeni razpored — potrditev velja naprej.'
                      : 'Povrnjeno na objavljeni razpored.',
                }),
            })
          }
          style={{ ...BTN, opacity: blocked ? 0.45 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}
        >
          Povrni na objavljeno
        </button>
      </div>
      <div style={{ fontSize: rem(11.5), color: 'oklch(0.55 0.012 90)', marginTop: 8 }}>
        {data.revertable === 0
          ? 'Povrniti ni česa — te spremembe pripadajo mestu in jih vidijo vsi, ki tam stojijo.'
          : blocked
            ? 'Povrnitev ni mogoča, dokler stojijo zgornji razlogi.'
            : v.confirmationStatus === 'čaka' && v.confirmedVersion > 0
            ? 'Objava jo bo znova vprašala za potrditev; povrnitev pusti veljati tisto, ki jo je že dala.'
            : 'Oboje je brez pošte — napotnica se pošilja posebej.'}
      </div>
    </div>
  );
}

const BTN: React.CSSProperties = {
  font: 'inherit',
  fontSize: rem(13),
  fontWeight: 600,
  borderRadius: 7,
  padding: '7px 13px',
  cursor: 'pointer',
  border: '1px solid oklch(0.86 0.008 95)',
  background: 'oklch(1 0 0)',
  color: 'oklch(0.3 0.012 90)',
};
